import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/Layout";
import { Plus, Archive, LayoutTemplate, Clock, Search } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";
import PriorityChip from "@/components/PriorityChip";
import { fmtDate } from "@/lib/dates";
import { DatePicker } from "@/components/DatePicker";

const BLANK_FORM = { title: "", description: "", due_date: "", assignee_ids: [], status: "Not Started", priority: "Medium", trigger_date: "", created_from_template_id: null };

export function TriggerPendingChip({ task }) {
  if (task?.trigger_status !== "pending") return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5"
      title={`Activates ${task.trigger_date} at 10:00 AM`}
      data-testid={`trigger-pending-${task.id}`}
    >
      <Clock size={9} /> {fmtDate(task.trigger_date)}
    </span>
  );
}

export default function TasksTab({ projectId, tasks, users, onOpenTask, onChanged }) {
  const { user } = useAuth();
  const isManagerUp = ["Admin", "Manager"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [view, setView] = useState("active"); // "active" | "archived"
  const [pickerOpen, setPickerOpen] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [tplSearch, setTplSearch] = useState("");

  const { active, archived } = useMemo(() => {
    const a = []; const r = [];
    for (const t of tasks) {
      (t.status === "Completed" ? r : a).push(t);
    }
    return { active: a, archived: r };
  }, [tasks]);

  const shown = view === "archived" ? archived : active;

  useEffect(() => {
    if (pickerOpen && templates === null) {
      api.get("/task-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
    }
  }, [pickerOpen, templates]);

  const filteredTemplates = useMemo(() => {
    const q = tplSearch.trim().toLowerCase();
    const list = templates || [];
    if (!q) return list;
    return list.filter((t) =>
      t.title.toLowerCase().includes(q)
      || (t.description || "").toLowerCase().includes(q)
      || (t.recurrence || "").toLowerCase().includes(q));
  }, [templates, tplSearch]);

  const applyTemplate = (tpl) => {
    setForm({
      title: tpl.title,
      description: tpl.description || "",
      priority: tpl.priority || "Medium",
      assignee_ids: tpl.default_assignee_ids || [],
      status: "Not Started",
      due_date: tpl.suggested?.due_date || "",
      trigger_date: tpl.suggested?.trigger_date || "",
      created_from_template_id: tpl.id,
    });
    setPickerOpen(false);
    setOpen(true);
  };

  const create = async () => {
    try {
      const body = { ...form, project_id: projectId };
      if (!body.due_date) delete body.due_date;
      if (!body.trigger_date) delete body.trigger_date;
      if (!body.created_from_template_id) delete body.created_from_template_id;
      await api.post("/tasks", body);
      toast.success("Task created");
      setOpen(false);
      setForm(BLANK_FORM);
      onChanged?.();
    } catch (e) { toast.error(parseApiError(e, "Failed")); }
  };

  const toggleAssignee = (uid) => {
    setForm((f) => {
      const ids = new Set(f.assignee_ids || []);
      if (ids.has(uid)) ids.delete(uid); else ids.add(uid);
      return { ...f, assignee_ids: [...ids] };
    });
  };

  const assigneeNames = (t) => {
    const ids = t.assignee_ids || [];
    if (ids.length === 0) return "Unassigned";
    const names = ids.map((id) => users.find((u) => u.user_id === id)?.name || id);
    if (names.length <= 2) return names.join(", ");
    return `${names[0]}, ${names[1]} +${names.length - 2}`;
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden" data-testid="task-view-toggle">
          <button
            onClick={() => setView("active")}
            className={`px-3 py-1.5 text-xs ${view === "active" ? "bg-[#0a2540] text-white" : "bg-white text-slate-600 hover:text-[#0a2540]"}`}
            data-testid="view-active"
          >
            Active <span className="ml-1 font-mono-data opacity-80">({active.length})</span>
          </button>
          <button
            onClick={() => setView("archived")}
            className={`px-3 py-1.5 text-xs inline-flex items-center gap-1 ${view === "archived" ? "bg-[#0a2540] text-white" : "bg-white text-slate-600 hover:text-[#0a2540]"}`}
            data-testid="view-archived"
          >
            <Archive size={11} /> Archived <span className="ml-1 font-mono-data opacity-80">({archived.length})</span>
          </button>
        </div>
        <div className="flex items-center gap-2">
          {isManagerUp && (
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => { setTplSearch(""); setPickerOpen(true); }}
              data-testid="btn-use-template"
            >
              <LayoutTemplate size={14} className="mr-1.5" /> Use Template
            </Button>
          )}
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(BLANK_FORM); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" data-testid="btn-new-task">
                <Plus size={14} className="mr-1.5" /> New Task
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>New Task</DialogTitle></DialogHeader>
              {form.created_from_template_id && (
                <div className="flex items-center gap-1.5 text-[11px] text-[#0a2540] bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-1.5 -mt-1" data-testid="task-template-source">
                  <LayoutTemplate size={11} /> Prefilled from template — review and adjust before creating.
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Title *</Label>
                  <Input className="mt-1 rounded-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="input-task-title" />
                </div>
                <div>
                  <Label className="text-xs">Description</Label>
                  <Textarea className="mt-1 rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="input-task-desc" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Due Date</Label>
                    <div className="mt-1"><DatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testId="input-task-due" /></div>
                  </div>
                  <div>
                    <Label className="text-xs">Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="mt-1 rounded-sm" data-testid="select-task-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {isManagerUp && (
                  <div>
                    <Label className="text-xs">Trigger Date</Label>
                    <div className="mt-1"><DatePicker value={form.trigger_date} onChange={(v) => setForm({ ...form, trigger_date: v })} testId="input-task-trigger" /></div>
                    <p className="text-[10px] text-slate-400 mt-1">
                      Task activates &amp; assignees are notified at 10:00 AM on this date. Blank = today.
                    </p>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Assignees</Label>
                  <div className="mt-1 border border-slate-200 rounded-sm p-2 max-h-40 overflow-y-auto space-y-1" data-testid="task-assignees-list">
                    {users.length === 0 && <div className="text-[11px] text-slate-400 px-1">No teammates available</div>}
                    {users.map((u) => (
                      <label key={u.user_id} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(form.assignee_ids || []).includes(u.user_id)}
                          onChange={() => toggleAssignee(u.user_id)}
                          data-testid={`task-assignee-${u.user_id}`}
                        />
                        <span className="truncate">{u.name}</span>
                        {u.email && <span className="text-slate-400 font-mono-data text-[10px] truncate">· {u.email}</span>}
                        <span className="text-slate-400 font-mono-data ml-auto shrink-0">({u.role})</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={create} data-testid="btn-create-task">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Template picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="template-picker">
          <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>Create task from template</DialogTitle></DialogHeader>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={tplSearch}
              onChange={(e) => setTplSearch(e.target.value)}
              placeholder="Search templates…"
              className="pl-8 rounded-sm"
              data-testid="template-picker-search"
            />
          </div>
          <div className="max-h-72 overflow-y-auto space-y-1 -mx-1 px-1">
            {templates === null && <div className="text-xs text-slate-400 py-4 text-center">Loading…</div>}
            {templates !== null && filteredTemplates.length === 0 && (
              <div className="text-xs text-slate-400 py-6 text-center">
                No templates found. Create them under <span className="font-medium">Task Templates</span> in the sidebar.
              </div>
            )}
            {filteredTemplates.map((tpl) => (
              <button
                key={tpl.id}
                onClick={() => applyTemplate(tpl)}
                className="w-full text-left border border-slate-200 rounded-sm px-3 py-2.5 hover:border-[#0a2540] hover:bg-slate-50 transition-colors"
                data-testid={`template-option-${tpl.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#0a2540] flex-1 truncate">{tpl.title}</span>
                  <PriorityChip priority={tpl.priority} />
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[10px] text-slate-500">
                  {tpl.recurrence && <span>{tpl.recurrence}</span>}
                  {tpl.trigger_day_of_month && <span className="inline-flex items-center gap-1"><Clock size={9} /> Day {tpl.trigger_day_of_month} → {fmtDate(tpl.suggested?.trigger_date)}</span>}
                  {tpl.due_after_days != null && <span>Due +{tpl.due_after_days}d</span>}
                  {tpl.usage_count > 0 && <span className="text-slate-400">Used {tpl.usage_count}×</span>}
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {view === "archived" && archived.length > 0 && (
        <div className="mb-3 text-[11px] text-slate-500 inline-flex items-center gap-1.5">
          <Archive size={11} /> Completed tasks are preserved here. Click any row to view details or re-open.
        </div>
      )}

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2" data-testid="task-cards-mobile">
        {shown.length === 0 && (
          <div className="border border-slate-200 px-4 py-10 text-center text-xs text-slate-400">
            {view === "archived" ? "No archived tasks" : "No tasks yet"}
          </div>
        )}
        {shown.map((t) => (
          <button
            key={t.id}
            onClick={() => onOpenTask(t)}
            className="w-full text-left border border-slate-200 px-3 py-2.5 hover:bg-slate-50 flex flex-col gap-1.5"
            data-testid={`task-card-${t.id}`}
          >
            <div className="text-sm font-medium text-[#0a2540] line-clamp-2">{t.title}</div>
            <div className="flex flex-wrap items-center gap-2 text-[10px]">
              <PriorityChip priority={t.priority} />
              <StatusBadge status={t.status} />
              <TriggerPendingChip task={t} />
              {view === "archived" && t.completed_at ? (
                <span className="font-mono-data text-emerald-700">Completed {fmtDate(t.completed_at)}</span>
              ) : (
                <span className="font-mono-data text-slate-500">{fmtDate(t.due_date)}</span>
              )}
              <span className="text-slate-400">·</span>
              <span className="text-slate-600 truncate">{assigneeNames(t)}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5">Task</th>
              <th className="text-left px-4 py-2.5">Priority</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-left px-4 py-2.5">{view === "archived" ? "Completed" : "Due"}</th>
              <th className="text-left px-4 py-2.5">Assignee</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-slate-400">
                {view === "archived" ? "No archived tasks" : "No tasks yet"}
              </td></tr>
            )}
            {shown.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => onOpenTask(t)} data-testid={`task-row-${t.id}`}>
                <td className="px-4 py-3 font-medium text-[#0a2540]">
                  <span className="inline-flex items-center gap-2">{t.title}<TriggerPendingChip task={t} /></span>
                </td>
                <td className="px-4 py-3"><PriorityChip priority={t.priority} /></td>
                <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                <td className="px-4 py-3 font-mono-data text-xs">
                  {view === "archived"
                    ? <span className="text-emerald-700">{fmtDate(t.completed_at)}</span>
                    : fmtDate(t.due_date)}
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{assigneeNames(t)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
